import { GripVertical, Trash2, Settings } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { ChecklistItemSchema, ChecklistItemTipo, ChecklistItemConfig } from '@portal-leo/shared';
import { useState } from 'react';

interface ItemBuilderProps {
  item: ChecklistItemSchema;
  onChange: (item: ChecklistItemSchema) => void;
  onRemove: () => void;
}

const tipoLabels: Record<ChecklistItemTipo, string> = {
  CHECKBOX: 'Checkbox',
  STATUS: 'Status',
  TEXTO: 'Texto',
  NUMERO: 'Numero',
  FOTO: 'Foto',
};

export function ItemBuilder({ item, onChange, onRemove }: ItemBuilderProps) {
  const [showConfig, setShowConfig] = useState(false);

  const handleNomeChange = (nome: string) => {
    onChange({ ...item, nome });
  };

  const handleTipoChange = (tipo: ChecklistItemTipo) => {
    const newConfig: ChecklistItemConfig = tipo === 'STATUS' ? { options: ['Conforme', 'Nao conforme', 'N/A'] } : undefined;
    onChange({ ...item, tipo, config: newConfig });
  };

  const handleConfigChange = (config: ChecklistItemConfig) => {
    onChange({ ...item, config });
  };

  const addOption = () => {
    const currentOptions = item.config?.options || [];
    handleConfigChange({ ...item.config, options: [...currentOptions, 'Nova opcao'] });
  };

  const updateOption = (index: number, value: string) => {
    const currentOptions = item.config?.options || [];
    const newOptions = [...currentOptions];
    newOptions[index] = value;
    handleConfigChange({ ...item.config, options: newOptions });
  };

  const removeOption = (index: number) => {
    const currentOptions = item.config?.options || [];
    handleConfigChange({ ...item.config, options: currentOptions.filter((_, i) => i !== index) });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
      <div className="flex items-center gap-2">
        <GripVertical className="h-4 w-4 text-gray-400 cursor-move flex-shrink-0" />

        <input
          type="text"
          value={item.nome}
          onChange={(e) => handleNomeChange(e.target.value)}
          placeholder="Nome do item"
          className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />

        <select
          value={item.tipo}
          onChange={(e) => handleTipoChange(e.target.value as ChecklistItemTipo)}
          className="px-2 py-1 text-sm border border-gray-200 rounded focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          {Object.entries(tipoLabels).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        {item.tipo === 'STATUS' && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowConfig(!showConfig)}
            title="Configurar opcoes"
          >
            <Settings className="h-4 w-4" />
            <span className="text-xs ml-1">({item.config?.options?.length || 0})</span>
          </Button>
        )}

        <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>

      {/* Date prazo */}
      <div className="flex items-center gap-2 mt-2 ml-6">
        <label className="text-xs text-gray-500 whitespace-nowrap">Prazo:</label>
        <input
          type="date"
          value={item.dataPrazo || ''}
          onChange={(e) => onChange({ ...item, dataPrazo: e.target.value || null })}
          className="px-2 py-1 text-sm border border-gray-200 rounded focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        {item.dataPrazo && (
          <button
            type="button"
            onClick={() => onChange({ ...item, dataPrazo: null })}
            className="text-xs text-red-500 hover:text-red-700"
          >
            Limpar
          </button>
        )}
      </div>

      {/* Status options config */}
      {item.tipo === 'STATUS' && showConfig && (
        <div className="mt-3 ml-6 p-3 bg-white rounded border border-gray-200">
          <p className="text-xs font-medium text-gray-700 mb-2">Opcoes de status</p>
          <div className="space-y-2">
            {(item.config?.options || []).map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={option}
                  onChange={(e) => updateOption(index, e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:border-primary-500 focus:outline-none"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeOption(index)}
                >
                  <Trash2 className="h-3 w-3 text-red-500" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addOption}>
              + Adicionar opcao
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
